/**
 * Animación de impresión: la impresora que saca el ticket en pantalla al mandar una
 * precuenta, una cuenta o un corte de caja. Opcional por restaurante (setting
 * "AnimacionImpresion", apagado por defecto); las comandas nunca la usan.
 *
 * Los servicios de impresión no son componentes de React y se llaman desde handlers, así
 * que esto es un emisor a nivel de módulo: quien imprime abre un handle y le avisa cómo
 * terminó; <PrintAnimationOverlay> (montado una vez en el layout) escucha y dibuja. Con el
 * setting apagado el overlay no se suscribe y `startPrintAnimation` regresa un handle mudo:
 * cero trabajo extra en la caja.
 */

/** Llave del setting (Boolean, default "false") en el seeder del backend. */
export const PRINT_ANIMATION_SETTING_KEY = 'AnimacionImpresion';

export type PrintTicketKind = 'precuenta' | 'cuenta' | 'corte';

export interface PrintTicketLine {
  left: string;
  right?: string;
  strong?: boolean;
  /** Subtotal/IVA: se ocultan cuando el restaurante apagó "Mostrar desglose de IVA". */
  taxDetail?: boolean;
}

export interface PrintTicketPreview {
  kind: PrintTicketKind;
  /** "PRECUENTA", "TICKET DE PAGO", "CORTE DE CAJA" */
  title: string;
  /** Bloques separados por una línea punteada, como en el papel. */
  sections: PrintTicketLine[][];
}

/**
 * printed: el agente o Bluetooth confirmaron. sent: la nube encoló el trabajo; si la
 * impresora falla después lo avisa <PrintStatusListener>, así que aquí no se dice "impreso".
 */
export type PrintOutcome = 'printed' | 'sent' | 'failed';

export type PrintAnimationEvent =
  | { type: 'start'; id: number; ticket: PrintTicketPreview }
  | { type: 'end'; id: number; outcome: PrintOutcome };

export interface PrintAnimationHandle {
  /** Cómo terminó; la segunda llamada se ignora. El aviso lo sigue dando quien imprime. */
  finish: (outcome: PrintOutcome) => void;
}

type Listener = (event: PrintAnimationEvent) => void;

const listeners = new Set<Listener>();
let nextId = 0;

const emit = (event: PrintAnimationEvent) => {
  listeners.forEach(listener => listener(event));
};

export const subscribePrintAnimation = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const NOOP_HANDLE: PrintAnimationHandle = { finish: () => {} };

/**
 * Abre la animación de un ticket. Llamarla sólo cuando de verdad se va a imprimir (hay
 * servicio disponible): sin impresora no hay papel que mostrar. El handle es de un solo
 * uso; la segunda llamada a printed/failed se ignora.
 */
export const startPrintAnimation = (ticket: PrintTicketPreview): PrintAnimationHandle => {
  if (listeners.size === 0) return NOOP_HANDLE;
  const id = ++nextId;
  let settled = false;
  emit({ type: 'start', id, ticket });
  return {
    finish: (outcome) => {
      if (settled) return;
      settled = true;
      emit({ type: 'end', id, outcome });
    },
  };
};

/**
 * Cómo terminó una llamada de impresión, leyendo lo que ya regresan los servicios:
 * agente y Bluetooth dan `{ success }`; la nube da el resultado del encolado, `undefined`
 * si falló o `jobIds` vacío si no había impresora para esos renglones.
 */
export const printOutcomeOf = (result: unknown): PrintOutcome => {
  if (result == null) return 'failed';
  if (typeof result === 'object') {
    const r = result as { success?: unknown; jobIds?: unknown };
    if (r.success === false) return 'failed';
    if (Array.isArray(r.jobIds)) return r.jobIds.length === 0 ? 'failed' : 'sent';
  }
  return 'printed';
};

/**
 * Envuelve UNA llamada a un servicio de impresión: regresa (o lanza) exactamente lo mismo
 * que la llamada, así que quien imprime conserva sus avisos y su manejo de errores.
 */
export const withPrintAnimation = async <T>(
  ticket: PrintTicketPreview,
  print: () => Promise<T>
): Promise<T> => {
  const handle = startPrintAnimation(ticket);
  try {
    const result = await print();
    handle.finish(printOutcomeOf(result));
    return result;
  } catch (error) {
    handle.finish('failed');
    throw error;
  }
};

/** "$1,284.00" — el mismo formato que el papel (escposCommands.formatCurrency) con miles. */
export const formatTicketMoney = (amount: number): string =>
  `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MAX_ITEM_LINES = 8;

export interface OrderTicketInput {
  kind: Extract<PrintTicketKind, 'precuenta' | 'cuenta'>;
  title: string;
  tableLabel: string;
  waiterName?: string;
  /** "#123" o el folio sin conexión ("SIN RED 7"); ver formatOrderNumber. */
  orderLabel?: string;
  items: { quantity: number; productName: string; total: number; isCancelled?: boolean }[];
  subtotal: number;
  tax: number;
  discounts?: { reason?: string; discountAmount: number }[];
  tip?: number;
  total: number;
  payments?: { method: string; amount: number }[];
}

/** Precuenta y ticket de pago: encabezado de la mesa, renglones y totales. */
export const buildOrderTicketPreview = (input: OrderTicketInput): PrintTicketPreview => {
  const head: PrintTicketLine[] = [{ left: input.tableLabel, right: input.orderLabel }];
  if (input.waiterName) head.push({ left: `Atendió: ${input.waiterName}` });

  const lines = input.items.filter(item => !item.isCancelled);
  // El papel en pantalla tiene alto fijo: con muchos renglones el TOTAL se saldría del
  // cuadro, y es lo que más importa ver. Los que sobran se resumen en una línea.
  const shown = lines.length > MAX_ITEM_LINES ? lines.slice(0, MAX_ITEM_LINES - 1) : lines;
  const items: PrintTicketLine[] = shown
    .map(item => ({ left: `${item.quantity} ${item.productName}`, right: formatTicketMoney(item.total) }));
  if (shown.length < lines.length) {
    items.push({ left: `… y ${lines.length - shown.length} renglones más` });
  }

  const totals: PrintTicketLine[] = [
    { left: 'Subtotal', right: formatTicketMoney(input.subtotal), taxDetail: true },
    { left: 'IVA', right: formatTicketMoney(input.tax), taxDetail: true },
    ...(input.discounts ?? []).map(d => ({ left: d.reason || 'Descuento', right: `-${formatTicketMoney(d.discountAmount)}` })),
    ...(input.tip ? [{ left: 'Propina', right: formatTicketMoney(input.tip) }] : []),
    { left: 'TOTAL', right: formatTicketMoney(input.total), strong: true },
  ];

  const sections = [head, items, totals];
  if (input.payments && input.payments.length > 0) {
    sections.push(input.payments.map(p => ({ left: p.method, right: formatTicketMoney(p.amount) })));
  }
  return { kind: input.kind, title: input.title, sections };
};
